// Client mirror of api/statement_analyzer/services/discount_rate.py: what the
// server totals become after the dock saves a discount rate. The shared fixture
// test keeps the two in step; change both together.
import type { LineItem } from '../api';

export type ViewMode = 'default' | 'user_edits' | 'customized_quote';

// The extra_attributes fields the projection reads.
interface FeeData {
	amount?: number | string | null;
	fee_due?: number | string | null;
	volume?: number | string | null;
}
interface LineExtra {
	fee_type?: string;
	card_network?: string;
	parent_id?: string | null;
	children?: unknown[];
	ic_plus_data?: FeeData;
	original_values?: { ic_plus_data?: FeeData };
	user_edits?: { ic_plus_data?: Record<string, unknown> };
	customized_quote?: Record<string, unknown>;
}

const extraOf = (item: LineItem): LineExtra =>
	(item.extra_attributes ?? {}) as LineExtra;

const DISCOUNT_FEE_TYPE = 'discount_rate';
const OVERRIDE_FIELDS = ['amount', 'fee', 'fee_due', 'rate'];
// Networks a same-for-all discount rate reaches (RATED_NETWORKS on the server).
const RATED_NETWORKS = new Set([
	'visa',
	'mastercard',
	'discover',
	'amex',
	'visa_debit',
	'mastercard_debit',
	'discover_debit',
	'pin_debit',
]);

export interface DiscountRateBase {
	proposedTotal: number;
	monthlySavings: number;
	volume: number;
}

export interface DiscountRateProjection {
	proposedTotal: number;
	effectiveRate: number;
	monthlySavings: number;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

const amountOf = (data: FeeData | null | undefined): number | null => {
	const value = data?.amount ?? data?.fee_due;
	return value == null ? null : Number(value);
};

const originalOf = (extra: LineExtra): FeeData | null => {
	const original = extra?.original_values?.ic_plus_data;
	return original && amountOf(original) != null ? original : null;
};

const isOverridden = (extra: LineExtra): boolean => {
	const edits = extra?.user_edits?.ic_plus_data ?? {};
	const quote = extra?.customized_quote ?? {};
	return OVERRIDE_FIELDS.some((field) => field in edits || field in quote);
};

/** Leaves the server will re-rate; the dock offers the slider only when there is one. */
export const discountLines = (items: LineItem[]): LineItem[] =>
	items.filter((item) => {
		const extra = extraOf(item);
		return (
			String(extra?.fee_type ?? '').toLowerCase() === DISCOUNT_FEE_TYPE &&
			RATED_NETWORKS.has(String(extra?.card_network ?? '').toLowerCase()) &&
			!extra?.children?.length &&
			Number(extra?.ic_plus_data?.volume) > 0 &&
			!isOverridden(extra)
		);
	});

/** Change in the proposed total, as totals_calculator sums root rows in this view. */
const totalDelta = (
	items: LineItem[],
	viewMode: ViewMode,
	ratePercent: number,
): number => {
	const byId = new Map(items.map((item) => [String(item.id), item]));
	return discountLines(items).reduce((sum, item) => {
		const extra = extraOf(item);
		const newAmount = Number(extra.ic_plus_data?.volume) * (ratePercent / 100);
		const delta = newAmount - (amountOf(extra.ic_plus_data) ?? 0);
		const original = originalOf(extra);
		const defaultDelta = original
			? newAmount - (amountOf(original) ?? 0)
			: delta;

		// The row the totals read: the leaf itself, or its parent.
		const parentId = extra.parent_id;
		const root = parentId == null ? item : byId.get(String(parentId));
		if (!root) return sum;
		const rootExtra = extraOf(root);
		if (viewMode === 'default') {
			return sum + (originalOf(rootExtra) ? defaultDelta : delta);
		}
		if (
			viewMode === 'customized_quote' &&
			root !== item &&
			'amount' in (rootExtra.customized_quote ?? {})
		) {
			return sum;
		}
		return sum + delta;
	}, 0);
};

export const projectDiscountRate = (
	items: LineItem[],
	viewMode: ViewMode,
	ratePercent: number,
	base: DiscountRateBase,
): DiscountRateProjection => {
	const delta = totalDelta(items, viewMode, ratePercent);
	const proposedTotal = base.proposedTotal + delta;
	return {
		proposedTotal: round2(proposedTotal),
		effectiveRate:
			base.volume > 0 ? round2((proposedTotal / base.volume) * 100) : 0,
		monthlySavings: round2(base.monthlySavings - delta),
	};
};
